import { useMemo } from 'react'
import { useQueries } from 'react-query'
import axios from 'src/utils/axios'
import { API_BASE_URL } from 'src/constants'
import { structuralKey } from 'src/api/somaticStructural'
import { dnaPartnersFromReconciliation, rnaIdsToLookUp, withLookedUpPairing } from './pairedRna'

const SUMMARY_PARAMS = { view: 'summary' }
const FIVE_MINUTES = 5 * 60 * 1000

const fetchSummary = async (rnaId) => {
  const { data } = await axios.get(`${API_BASE_URL}/structural/${rnaId}/reconciliation`, { params: SUMMARY_PARAMS })
  return data
}

// Pairing for a page of the analysis list while the list payload does not carry
// it: one reconciliation summary per RNA row on the page (there are few), in
// parallel and cached for five minutes. A lookup that is pending or failed
// leaves its RNA row as it is today, so the list never waits on them. Once the
// payload carries `paired_rna` / `paired_dna_ids`, no lookup runs.
const usePairedRnaFallback = (rows) => {
  const rnaIds = useMemo(() => rnaIdsToLookUp(rows), [rows])
  const results = useQueries(
    rnaIds.map((rnaId) => ({
      queryKey: structuralKey(rnaId, 'reconciliation', SUMMARY_PARAMS),
      queryFn: () => fetchSummary(rnaId),
      staleTime: FIVE_MINUTES,
      cacheTime: FIVE_MINUTES * 2,
      retry: false,
      refetchOnWindowFocus: false,
    })),
  )

  // Stable across renders unless an answer changed.
  const answersKey = results.map((r) => r.dataUpdatedAt || 0).join(',')
  return useMemo(() => {
    const partnersByRnaId = {}
    rnaIds.forEach((rnaId, i) => {
      const data = results[i]?.data
      if (data) partnersByRnaId[String(rnaId)] = dnaPartnersFromReconciliation(rnaId, data)
    })
    return withLookedUpPairing(rows, partnersByRnaId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, rnaIds, answersKey])
}

export default usePairedRnaFallback
